import "./style/app-link.scss"

import React, { CSSProperties } from "react"
import classNames from "classnames"
import { useHistory, useLocation } from "react-router-dom"
import { saveScrollOffset } from "@app/helpers"

import { UniversalLink } from "@ng-mw/shared-react-components/shared"

export type AppLinkSkin = "none" | "button" | "link"

interface AppLinkProps {
    asButton?: boolean
    children: React.ReactNode
    className?: string
    from?: string
    instant?: boolean
    onClick?: () => void
    onTouchEnd?: (e: React.TouchEvent) => void
    onTouchMove?: () => void
    onTouchStart?: () => void
    pseudoHistory?: string[]
    replace?: boolean
    skin?: AppLinkSkin
    state?: object
    style?: CSSProperties
    to?: string
    url?: string
}

const AppLink = ({
    asButton = false,
    children,
    className,
    from,
    instant,
    onClick = () => undefined,
    onTouchEnd = () => undefined,
    onTouchMove = () => undefined,
    onTouchStart = () => undefined,
    pseudoHistory,
    replace = false,
    skin = "none",
    state,
    style,
    to,
    url,
}: AppLinkProps) => {
    const history = useHistory()
    const location = useLocation<object>()

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (onClick) {
            onClick()
        }

        if (to && location.pathname !== to) {
            e.preventDefault()
            e.stopPropagation()

            saveScrollOffset(history, location)

            if (pseudoHistory) {
                pseudoHistory.forEach(item => {
                    history.push({ pathname: item, state: {} })
                })
            }
            history[replace ? "replace" : "push"]({
                pathname: to,
                state: { ...state, from, isImmediate: instant },
            })
        }
    }

    const elementClassName = classNames("app-link", `app-link--skin-${skin}`, className, {
        "app-link--as-button": asButton,
    })

    if (url) {
        return (
            <UniversalLink
                className={elementClassName}
                destinationUrl={url}
            >
                {children}
            </UniversalLink>
        )
    }

    if (asButton) {
        return (
            <button
                type="button"
                className={elementClassName}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                onTouchMove={onTouchMove}
                onClick={handleClick}
                style={style}
            >
                {children}
            </button>
        )
    }

    return (
        <span
            className={elementClassName}
            role="link"
            tabIndex={0}
            aria-current={location.pathname === to}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onTouchMove={onTouchMove}
            onClick={handleClick}
            onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && handleClick(e)}
            style={style}
        >
            {children}
        </span>
    )
}

export default AppLink
